import { type DirectoryWalkEntry } from "./recursiveDirectoryWalk";

export interface DirectoryTreeNode {
  id: string;
  kind: "directory" | "file";
  name: string;
  relativePath: string;
  sizeBytes?: number;
  children: DirectoryTreeNode[];
}

function compareNodes(left: DirectoryTreeNode, right: DirectoryTreeNode): number {
  if (left.kind !== right.kind) {
    return left.kind === "directory" ? -1 : 1;
  }
  return left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
}

function sortTree(nodes: DirectoryTreeNode[]): void {
  nodes.sort(compareNodes);
  for (let index = 0; index < nodes.length; index += 1) {
    if (nodes[index].children.length > 0) {
      sortTree(nodes[index].children);
    }
  }
}

export function buildDirectoryTree(entries: DirectoryWalkEntry[]): DirectoryTreeNode[] {
  const roots: DirectoryTreeNode[] = [];
  const nodeByPath = new Map<string, DirectoryTreeNode>();

  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index];
    const segments = entry.relativePath.replace(/\\/g, "/").split("/").filter(Boolean);
    if (segments.length === 0) {
      continue;
    }

    let parentChildren = roots;
    let accumulated = "";

    for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex += 1) {
      const segment = segments[segmentIndex];
      accumulated = accumulated ? `${accumulated}/${segment}` : segment;
      const isLeaf = segmentIndex === segments.length - 1;

      let node = nodeByPath.get(accumulated);
      if (!node) {
        node = {
          id: accumulated,
          kind: isLeaf ? entry.kind : "directory",
          name: segment,
          relativePath: accumulated,
          sizeBytes: isLeaf && entry.kind === "file" ? entry.sizeBytes : undefined,
          children: [],
        };
        nodeByPath.set(accumulated, node);
        parentChildren.push(node);
      } else if (isLeaf && entry.kind === "file") {
        node.kind = "file";
        node.sizeBytes = entry.sizeBytes;
      }

      parentChildren = node.children;
    }
  }

  sortTree(roots);
  return roots;
}

export function collectFolderIds(nodes: DirectoryTreeNode[]): string[] {
  const ids: string[] = [];
  const visit = (nodeList: DirectoryTreeNode[]) => {
    for (let index = 0; index < nodeList.length; index += 1) {
      const node = nodeList[index];
      if (node.kind === "directory") {
        ids.push(node.id);
        visit(node.children);
      }
    }
  };
  visit(nodes);
  return ids;
}
